/**
 * Transforma o logo do cliente na máscara da marca d'água do fundo.
 *
 * POR QUE ISSO EXISTE
 * A parede do sistema é recortada com `mask-image`, que usa o canal de
 * TRANSPARÊNCIA da imagem. O logo da Onix está salvo como JPEG, e JPEG não tem
 * transparência — o recorte virava o quadrado inteiro da imagem, e a parede
 * ficava com um borrão retangular em vez da silhueta da marca.
 *
 * Aqui a transparência é derivada do próprio desenho, no navegador, sem tocar
 * no arquivo que está no Firestore. Funciona para os logos já enviados e para
 * os que vierem, em qualquer formato.
 *
 * Três casos, decididos por leitura dos pixels:
 *   1. já tem transparência (PNG/SVG bem exportado) → respeita a do arquivo
 *   2. desenho claro sobre fundo escuro (o caso da Onix) → brilho vira opacidade
 *   3. desenho escuro sobre fundo claro → brilho INVERTIDO vira opacidade
 *
 * O caso 3 importa: sem ele, um logo preto em fundo branco produziria o
 * negativo — a moldura apareceria e a marca sumiria.
 */
import { useEffect, useState } from 'react'

export interface WatermarkOpts {
  /** Lado do logo dentro da célula do padrão, em px. */
  logoPx: number
  /** Respiro em volta do logo. É o que separa uma marca da outra. */
  gapPx: number
}

/** Cache por (url + medidas): o desenho da célula não muda entre telas. */
const cache = new Map<string, string>()

/** Luminância perceptual (ITU-R BT.709) de um pixel RGB, de 0 a 1. */
function luminancia(r: number, g: number, b: number) {
  return (r * 0.2126 + g * 0.7152 + b * 0.0722) / 255
}

/**
 * Lê a imagem e devolve uma versão só-silhueta: branca, com a transparência
 * descrevendo o desenho. Branco porque a cor final vem do CSS
 * (`--watermark-tint`), que acompanha o tema claro/escuro.
 */
function extrairSilhueta(img: HTMLImageElement): HTMLCanvasElement | null {
  const c = document.createElement('canvas')
  c.width = img.naturalWidth
  c.height = img.naturalHeight
  const ctx = c.getContext('2d', { willReadFrequently: true })
  if (!ctx) return null
  ctx.drawImage(img, 0, 0)

  let dados: ImageData
  try {
    dados = ctx.getImageData(0, 0, c.width, c.height)
  } catch {
    return null // canvas contaminado por imagem de outra origem
  }
  const p = dados.data

  // O arquivo já traz transparência própria? Então ela manda.
  let temAlpha = false
  for (let i = 3; i < p.length; i += 4 * 97) {   // amostra esparsa, passo primo
    if (p[i] < 250) { temAlpha = true; break }
  }
  if (temAlpha) {
    for (let i = 0; i < p.length; i += 4) {
      p[i] = p[i + 1] = p[i + 2] = 255
    }
    ctx.putImageData(dados, 0, 0)
    return c
  }

  // Sem alpha: o fundo é o que estiver nas bordas. Se a borda for clara, o
  // desenho é escuro, e a conta precisa ser invertida.
  let somaBorda = 0
  let n = 0
  const w = c.width, h = c.height
  const emBorda = (x: number, y: number) => {
    const i = (y * w + x) * 4
    somaBorda += luminancia(p[i], p[i + 1], p[i + 2])
    n++
  }
  for (let x = 0; x < w; x += Math.max(1, Math.floor(w / 40))) {
    emBorda(x, 0); emBorda(x, h - 1)
  }
  for (let y = 0; y < h; y += Math.max(1, Math.floor(h / 40))) {
    emBorda(0, y); emBorda(w - 1, y)
  }
  const fundoClaro = n > 0 && somaBorda / n > 0.5

  for (let i = 0; i < p.length; i += 4) {
    const lum = luminancia(p[i], p[i + 1], p[i + 2])
    const bruto = fundoClaro ? 1 - lum : lum
    // Curva: joga o fundo fora de vez e deixa o traço cheio. Sem ela, o
    // gradiente do fundo do JPEG viraria uma névoa em volta da marca.
    const a = Math.max(0, Math.min(1, (bruto - 0.3) / 0.55))
    p[i] = p[i + 1] = p[i + 2] = 255
    p[i + 3] = Math.round(a * 255)
  }
  ctx.putImageData(dados, 0, 0)
  return c
}

/**
 * Monta UMA célula do padrão: a silhueta no meio de um quadrado maior.
 * O respiro entre as marcas é a moldura vazia da célula — assim o tamanho do
 * logo e o espaço entre eles ficam independentes, e a tela de Aparência pode
 * ajustar cada um sem mexer no outro.
 */
function montarCelula(silhueta: HTMLCanvasElement, logoPx: number, gapPx: number): string {
  const lado = Math.max(1, Math.round(logoPx + gapPx))
  const c = document.createElement('canvas')
  c.width = lado
  c.height = lado
  const ctx = c.getContext('2d')
  if (!ctx) return ''
  const r = Math.min(logoPx / silhueta.width, logoPx / silhueta.height)
  const w = silhueta.width * r
  const h = silhueta.height * r
  ctx.drawImage(silhueta, (lado - w) / 2, (lado - h) / 2, w, h)
  return c.toDataURL('image/png')
}

/**
 * Devolve a imagem (data URL) usada como máscara da parede, ou null enquanto
 * carrega / se não houver logo. Nada é gravado: tudo acontece no navegador.
 */
export function useWatermark(logoUrl: string | undefined, { logoPx, gapPx }: WatermarkOpts) {
  const [mascara, setMascara] = useState<string | null>(null)

  useEffect(() => {
    if (!logoUrl) { setMascara(null); return }

    const chave = `${logoUrl}|${logoPx}|${gapPx}`
    const pronto = cache.get(chave)
    if (pronto) { setMascara(pronto); return }

    let vivo = true
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => {
      if (!vivo) return
      const silhueta = extrairSilhueta(img)
      // Sem canvas utilizável, usa o arquivo cru: volta ao comportamento
      // antigo em vez de deixar a tela sem fundo nenhum.
      const url = silhueta ? montarCelula(silhueta, logoPx, gapPx) : logoUrl
      cache.set(chave, url)
      setMascara(url)
    }
    img.onerror = () => { if (vivo) setMascara(null) }
    img.src = logoUrl

    return () => { vivo = false }
  }, [logoUrl, logoPx, gapPx])

  return mascara
}
